// Fase de grupos: cómo se reparten las parejas, qué partidos se juegan y cómo
// queda la tabla. Todo puro: lo usan la API (para validar y para el sorteo de
// desempate) y la web (para mostrar), y así las dos dicen lo mismo.
//
// Hechos vs. derivados: los grupos armados y los resultados son hechos (se
// guardan). La tabla, los clasificados y los W.O. de una pareja que abandonó
// se CALCULAN acá cada vez.

import { cuenta, ganador, type Marcador } from './marcador';

export const GRUPO_MIN = 3;
export const GRUPO_MAX = 6;

// ── Cuántos grupos y de qué tamaño ────────────────────────────────────────

/**
 * Las maneras razonables de repartir `n` parejas: grupos parejos (se
 * diferencian en 1 como mucho), entre GRUPO_MIN y GRUPO_MAX. El organizador
 * elige (decidido por Marcos: "a veces son de 4 y a veces de 3").
 *
 *   13 -> [3,3,3,4] (4 grupos)  ·  [4,4,5] (3 grupos)
 *   12 -> [3,3,3,3]  ·  [4,4,4]  ·  [6,6]
 *
 * Los más grandes van al final: los grupos A, B... quedan con el tamaño más común.
 */
export function opcionesDeGrupos(n: number): number[][] {
  const opciones: number[][] = [];
  for (let k = Math.floor(n / GRUPO_MIN); k >= 1; k--) {
    const base = Math.floor(n / k);
    const sobran = n % k;
    if (base < GRUPO_MIN || base + (sobran ? 1 : 0) > GRUPO_MAX) continue;
    opciones.push([...Array(k - sobran).fill(base), ...Array(sobran).fill(base + 1)]);
  }
  return opciones;
}

/** "4 grupos de 3", "4 grupos: 3 de 3 y 1 de 4" */
export function describirOpcion(tamanios: readonly number[]): string {
  const cuantos = new Map<number, number>();
  for (const t of tamanios) cuantos.set(t, (cuantos.get(t) ?? 0) + 1);
  const total = tamanios.length === 1 ? '1 grupo' : `${tamanios.length} grupos`;
  if (cuantos.size === 1) return `${total} de ${tamanios[0]}`;
  return `${total}: ${[...cuantos].map(([t, c]) => `${c} de ${t}`).join(' y ')}`;
}

/** A, B, C... y si algún día hay más de 26 grupos, G27, G28... */
export function letraDeGrupo(i: number): string {
  return i < 26 ? String.fromCharCode(65 + i) : `G${i + 1}`;
}

// ── El sorteo ─────────────────────────────────────────────────────────────

export interface ParejaParaSorteo {
  id: number;
  cabeza_serie: number | null;
}

/**
 * Reparte las parejas en grupos de los tamaños pedidos.
 *
 * Las cabezas de serie van primero y en serpentina (1->A, 2->B, 3->C, 4->D,
 * 5->D, 6->C...), así dos favoritas no caen juntas. El resto se sortea.
 * `azar` se inyecta para poder probarlo; en la app es Math.random, porque el
 * sorteo lo mira y lo confirma el organizador antes de guardarlo.
 */
export function sortearGrupos(
  parejas: readonly ParejaParaSorteo[],
  tamanios: readonly number[],
  azar: () => number = Math.random,
): number[][] {
  const total = tamanios.reduce((s, t) => s + t, 0);
  if (total !== parejas.length) throw new Error(`Los grupos suman ${total} y hay ${parejas.length} parejas.`);

  const grupos: number[][] = tamanios.map(() => []);
  const cabezas = parejas.filter((p) => p.cabeza_serie !== null).sort((x, y) => x.cabeza_serie! - y.cabeza_serie!);
  const resto = parejas.filter((p) => p.cabeza_serie === null).map((p) => p.id);

  const k = tamanios.length;
  cabezas.forEach((p, i) => {
    const vuelta = Math.floor(i / k);
    let g = vuelta % 2 === 0 ? i % k : k - 1 - (i % k);
    // Si ese grupo ya se llenó (grupos de tamaños distintos), el siguiente con lugar.
    while (grupos[g]!.length >= tamanios[g]!) g = (g + 1) % k;
    grupos[g]!.push(p.id);
  });

  // Fisher-Yates para el resto.
  for (let i = resto.length - 1; i > 0; i--) {
    const j = Math.floor(azar() * (i + 1));
    [resto[i], resto[j]] = [resto[j]!, resto[i]!];
  }
  let r = 0;
  grupos.forEach((g, i) => {
    while (g.length < tamanios[i]!) g.push(resto[r++]!);
  });
  return grupos;
}

/**
 * Completa los grupos al azar SIN mover lo que ya está puesto. Es el camino
 * de los torneos privados: el organizador ubica a mano las parejas que quiere
 * (por ejemplo, las de menor nivel juntas) y la app reparte el resto.
 *
 * - `puestas[g]`: las parejas que ya están en el grupo g. Se respetan tal cual.
 * - Las cabezas de serie que faltan ubicar van al grupo con lugar que tenga
 *   menos cabezas (así siguen separadas mientras se pueda).
 * - El resto se sortea en los lugares libres, grupo por grupo.
 *
 * Un grupo con más parejas puestas que su tamaño se deja como está (lo decidió
 * el organizador); en ese caso a algún otro le va a faltar una, y eso lo avisa
 * `problemaConReparto` antes de guardar.
 */
export function completarGrupos(
  parejas: readonly ParejaParaSorteo[],
  tamanios: readonly number[],
  puestas: readonly (readonly number[])[],
  azar: () => number = Math.random,
): number[][] {
  const grupos = tamanios.map((_, i) => [...(puestas[i] ?? [])]);
  const ya = new Set(grupos.flat());
  const cabezaDe = new Map(parejas.map((p) => [p.id, p.cabeza_serie]));
  const cabezasEn = (g: number[]) => g.filter((id) => (cabezaDe.get(id) ?? null) !== null).length;
  const conLugar = () => grupos.map((g, i) => ({ g, i })).filter(({ g, i }) => g.length < tamanios[i]!);

  const faltan = parejas.filter((p) => !ya.has(p.id));
  const cabezas = faltan.filter((p) => p.cabeza_serie !== null).sort((x, y) => x.cabeza_serie! - y.cabeza_serie!);
  for (const p of cabezas) {
    const destino = conLugar().sort((x, y) => cabezasEn(x.g) - cabezasEn(y.g) || x.i - y.i)[0];
    if (!destino) break; // no queda lugar: quedan sin grupo y el organizador decide
    destino.g.push(p.id);
    ya.add(p.id);
  }

  const resto = faltan.filter((p) => !ya.has(p.id)).map((p) => p.id);
  for (let i = resto.length - 1; i > 0; i--) {
    const j = Math.floor(azar() * (i + 1));
    [resto[i], resto[j]] = [resto[j]!, resto[i]!];
  }
  let r = 0;
  grupos.forEach((g, i) => {
    while (g.length < tamanios[i]! && r < resto.length) g.push(resto[r++]!);
  });
  return grupos;
}

/** Lo que tiene que cumplir un reparto para guardarse (venga del sorteo o movido a mano). */
export function problemaConReparto(
  grupos: readonly (readonly number[])[],
  inscriptas: readonly number[],
  pasanPorGrupo: number,
): string | null {
  if (grupos.length === 0) return 'No hay grupos.';
  const vistas = new Set<number>();
  for (const g of grupos) {
    if (g.length < GRUPO_MIN) return `Un grupo tiene ${g.length} parejas: el mínimo es ${GRUPO_MIN}.`;
    if (g.length > GRUPO_MAX) return `Un grupo tiene ${g.length} parejas: el máximo es ${GRUPO_MAX}.`;
    if (g.length <= pasanPorGrupo) return `Pasan ${pasanPorGrupo} por grupo: cada grupo necesita al menos ${pasanPorGrupo + 1}.`;
    for (const id of g) {
      if (vistas.has(id)) return 'Una pareja está en dos grupos.';
      vistas.add(id);
    }
  }
  const faltan = inscriptas.filter((id) => !vistas.has(id)).length;
  if (faltan) return `Quedan ${faltan} pareja(s) sin grupo.`;
  if (vistas.size !== inscriptas.length) return 'Hay parejas en los grupos que no están inscriptas.';
  return null;
}

// ── Los partidos del grupo ────────────────────────────────────────────────

/**
 * Todos contra todos, por fechas (método del círculo): en cada fecha nadie
 * juega dos veces, así en un grupo par los dos partidos de una fecha se juegan
 * a la vez en dos canchas. (Que nadie juegue dos seguidos es imposible con 4.)
 * Devuelve posiciones dentro del grupo (0 = la primera).
 *
 *   3 -> [1,2] · [0,2] · [0,1]  (con una "libre" por fecha)
 *   4 -> [0,3] [1,2] · [0,2] [1,3] · [0,1] [2,3]
 */
export function partidosDelGrupo(tamanio: number): [number, number][] {
  const n = tamanio % 2 === 0 ? tamanio : tamanio + 1; // impar: se suma una "libre"
  const libre = tamanio % 2 === 0 ? -1 : tamanio;
  const rueda = Array.from({ length: n }, (_, i) => i);
  const partidos: [number, number][] = [];
  for (let fecha = 0; fecha < n - 1; fecha++) {
    for (let i = 0; i < n / 2; i++) {
      const a = rueda[i]!;
      const b = rueda[n - 1 - i]!;
      if (a !== libre && b !== libre) partidos.push([Math.min(a, b), Math.max(a, b)]);
    }
    // Rota todos menos el primero.
    rueda.splice(1, 0, rueda.pop()!);
  }
  return partidos;
}

// ── La tabla ──────────────────────────────────────────────────────────────

export interface ParejaDeGrupo {
  id: number;
  /** Dio de baja con el grupo armado: los partidos que le faltan son W.O. para el rival. */
  abandono: boolean;
}

export interface PartidoDeGrupo {
  id: number;
  a: number; // id de pareja
  b: number;
  marcador: Marcador | null; // null = pendiente
}

export interface FilaTabla {
  id: number;
  pj: number;
  pg: number;
  pp: number;
  setsFavor: number;
  setsContra: number;
  gamesFavor: number;
  gamesContra: number;
  abandono: boolean;
  /** Cómo se lo desempató, si hizo falta: "le ganó a ...", "dif. de sets +1", "sorteo". */
  motivo: string | null;
  /** id de la pareja a la que le ganó el partido entre ellos (para que la pantalla ponga el nombre). */
  leGanoA: number | null;
}

export interface Tabla {
  filas: FilaTabla[];
  /** Todos los partidos tienen resultado (jugado o W.O.). */
  completo: boolean;
  /** Parejas empatadas en todo que necesitan el sorteo para ordenarse. Vacío = no hace falta. */
  faltaSorteo: number[];
  /** Los que pasan al cuadro. Solo con el grupo completo y sin sorteo pendiente. */
  clasificados: number[];
}

/**
 * El resultado que cuenta: el cargado, o un W.O. derivado si una de las dos
 * abandonó y el partido no se había jugado (decidido por Marcos: lo jugado se
 * mantiene, lo que falta es W.O.). Si abandonaron las dos, no cuenta.
 */
export function resultadoQueCuenta(p: PartidoDeGrupo, abandonos: ReadonlySet<number>): Marcador | null {
  if (p.marcador) return p.marcador;
  const aFuera = abandonos.has(p.a);
  const bFuera = abandonos.has(p.b);
  if (aFuera && !bFuera) return { wo: 'b', sets: null, tb: null };
  if (bFuera && !aFuera) return { wo: 'a', sets: null, tb: null };
  return null;
}

/**
 * La tabla del grupo. Orden (decidido por Marcos el 2026-09-30):
 *
 *   1. partidos ganados
 *   2. si empatan DOS: el partido entre ellos
 *   3. si empatan tres o más (o dos sin partido entre ellos): diferencia de
 *      sets y después de games, sobre TODOS los partidos del grupo
 *   4. lo que siga empatado: el sorteo guardado (`sorteo`: id -> orden)
 *
 * El que abandonó va siempre al fondo y nunca clasifica.
 */
export function tablaDelGrupo(
  parejas: readonly ParejaDeGrupo[],
  partidos: readonly PartidoDeGrupo[],
  pasan: number,
  sorteo: ReadonlyMap<number, number> = new Map(),
): Tabla {
  const abandonos = new Set(parejas.filter((p) => p.abandono).map((p) => p.id));
  const filas = new Map<number, FilaTabla>(
    parejas.map((p) => [
      p.id,
      { id: p.id, pj: 0, pg: 0, pp: 0, setsFavor: 0, setsContra: 0, gamesFavor: 0, gamesContra: 0, abandono: p.abandono, motivo: null, leGanoA: null },
    ]),
  );

  const resultados = new Map<number, Marcador>();
  let completo = true;
  for (const p of partidos) {
    const m = resultadoQueCuenta(p, abandonos);
    if (!m) {
      // Dos que abandonaron: ese partido no se juega nunca, no deja el grupo abierto.
      if (!(abandonos.has(p.a) && abandonos.has(p.b))) completo = false;
      continue;
    }
    resultados.set(p.id, m);
    const A = filas.get(p.a);
    const B = filas.get(p.b);
    if (!A || !B) continue;
    const c = cuenta(m);
    A.pj++;
    B.pj++;
    A.setsFavor += c.setsA;
    A.setsContra += c.setsB;
    B.setsFavor += c.setsB;
    B.setsContra += c.setsA;
    A.gamesFavor += c.gamesA;
    A.gamesContra += c.gamesB;
    B.gamesFavor += c.gamesB;
    B.gamesContra += c.gamesA;
    if (ganador(m) === 'a') {
      A.pg++;
      B.pp++;
    } else {
      B.pg++;
      A.pp++;
    }
  }

  const difSets = (f: FilaTabla) => f.setsFavor - f.setsContra;
  const difGames = (f: FilaTabla) => f.gamesFavor - f.gamesContra;
  const signo = (n: number) => (n > 0 ? `+${n}` : `${n}`);

  const faltaSorteo: number[] = [];

  /** Ordena un bloque empatado en ganados. */
  const desempatar = (bloque: FilaTabla[]): FilaTabla[] => {
    if (bloque.length === 1) return bloque;
    const hayPartidos = bloque.some((f) => f.pj > 0);

    if (bloque.length === 2 && hayPartidos) {
      const [x, y] = bloque as [FilaTabla, FilaTabla];
      const entre = partidos.find((p) => (p.a === x.id && p.b === y.id) || (p.a === y.id && p.b === x.id));
      const m = entre && resultados.get(entre.id);
      if (entre && m) {
        const gana = ganador(m) === 'a' ? entre.a : entre.b;
        const [primero, segundo] = gana === x.id ? [x, y] : [y, x];
        primero.motivo = 'le ganó';
        primero.leGanoA = segundo.id;
        return [primero, segundo];
      }
    }

    // Sets y games sobre todo el grupo; lo que siga igual, sorteo.
    const ordenado = [...bloque].sort((x, y) => difSets(y) - difSets(x) || difGames(y) - difGames(x));
    const salida: FilaTabla[] = [];
    for (let i = 0; i < ordenado.length; ) {
      const igual = ordenado.filter((f) => difSets(f) === difSets(ordenado[i]!) && difGames(f) === difGames(ordenado[i]!));
      if (igual.length === 1) {
        const f = igual[0]!;
        if (hayPartidos) {
          // Se dice lo que lo separó: los sets si alcanzaron, si no los games.
          const mismosSets = ordenado.filter((g) => difSets(g) === difSets(f)).length > 1;
          f.motivo = mismosSets ? `dif. de games ${signo(difGames(f))}` : `dif. de sets ${signo(difSets(f))}`;
        }
      } else if (hayPartidos) {
        const conSorteo = igual.every((f) => sorteo.has(f.id));
        if (conSorteo) {
          igual.sort((x, y) => sorteo.get(x.id)! - sorteo.get(y.id)!);
          igual.forEach((f) => (f.motivo = 'sorteo'));
        } else {
          faltaSorteo.push(...igual.map((f) => f.id));
        }
      }
      salida.push(...igual);
      i += igual.length;
    }
    return salida;
  };

  const enJuego = [...filas.values()].filter((f) => !f.abandono).sort((x, y) => y.pg - x.pg);
  const afuera = [...filas.values()].filter((f) => f.abandono);

  const ordenadas: FilaTabla[] = [];
  for (let i = 0; i < enJuego.length; ) {
    const bloque = enJuego.filter((f) => f.pg === enJuego[i]!.pg);
    ordenadas.push(...desempatar(bloque));
    i += bloque.length;
  }
  ordenadas.push(...afuera);

  // Un sorteo que falta solo frena si toca la línea de los que pasan.
  const importa = faltaSorteo.some((id) => {
    const pos = ordenadas.findIndex((f) => f.id === id);
    return pos < pasan;
  });
  const sorteoPendiente = importa ? faltaSorteo : [];

  const clasificados =
    completo && sorteoPendiente.length === 0 ? ordenadas.filter((f) => !f.abandono).slice(0, pasan).map((f) => f.id) : [];

  // Sin partidos jugados, ningún motivo tiene sentido.
  return { filas: ordenadas, completo, faltaSorteo: completo ? sorteoPendiente : [], clasificados };
}
